/* OLA — brand colours for the Flashy reviews widget.
 *
 * WHY THIS FILE EXISTS
 * Flashy renders <flashy-customer-reviews> into a shadow root, so nothing in the
 * theme's stylesheets can reach it: ordinary selectors stop at the shadow boundary.
 * Two things do cross it — inherited properties, and custom properties — and Flashy
 * exposes exactly one of the latter:
 *
 *     .bg-primary { background-color: var(--fl-branding-actionBg, #2C86FF); }
 *
 * That one is already correct: it resolves to #ddc0b4 on this store because the
 * brand colour is set in Flashy's own dashboard, which is where it belongs. What it
 * does not cover is the surfaces — the widget paints a white card, and the page it
 * sits on is cream — so the block reads as a component borrowed from another site.
 *
 * WHAT THIS DOES, AND WHAT IT DELIBERATELY DOES NOT
 * The shadow root is open and adoptedStyleSheets is writable, so a stylesheet can be
 * appended to it. Everything below is written against ELEMENT selectors and :host,
 * never against Flashy's Tailwind class names. That is the whole point: utility
 * classes like .bg-\[\#2C86FF\] are an implementation detail that can change in any
 * release of theirs, and a rule keyed to one would break silently. section, button,
 * select and input are part of the widget's contract with the page.
 *
 * If Flashy ever ships more --fl-branding-* variables, prefer those over anything
 * here and delete the matching block.
 */
(function () {
  'use strict';

  var TAG = 'flashy-customer-reviews';
  var MARK = '__olaBrandSheet';

  var CSS = [
    ':host {',
    '  --fl-branding-actionBg: #ddc0b4;',
    '  font-family: inherit;',
    '}',
    /* The widget's own card is dropped: it already sits inside .product-reviews,
       which supplies the band. Two nested surfaces read as a box in a box. */
    'section {',
    '  background: transparent !important;',
    '  color: #1c1c1c !important;',
    '  padding: 0 !important;',
    '}',
    /* Controls get the shop's pill shape and its readable brown on nude. White on
       #ddc0b4 measures 1.71:1, which is why it is named here rather than left alone. */
    'button, select {',
    '  font-family: inherit !important;',
    '  border-radius: 40px !important;',
    '  border-color: rgba(72, 72, 72, 0.3) !important;',
    '}',
    /* This rule used to be 'button { color: #5C3228 !important }', and it is what made
       the load-more button unreadable. Flashy paints that button with an INLINE style,
       style="color:#ffffff;background-color:#484848", and an inline declaration beats a
       stylesheet rule that is not !important. So our colour won and their background
       stayed: #5C3228 brown on #484848 grey, about 1.4:1, which is the dark button Guy
       photographed. Forcing a colour onto a background we do not control is the mistake;
       the pair has to be set together or not at all.

       So the blanket colour override is gone, and the one button that needs restyling is
       named and given both halves. It is now the same outline pill the shop uses
       everywhere else, and the same one .box-reviews__more uses on the box pages, so the
       two review walls end with an identical control. */
    '.pagination-button, button[class*="pagination"] {',
    '  background-color: transparent !important;',
    '  background-image: none !important;',
    '  color: #5C3228 !important;',
    '  border: 1.5px solid #c4a99d !important;',
    '  border-radius: 40px !important;',
    '  padding: 11px 26px !important;',
    '  min-height: 44px !important;',
    '  font-size: 13px !important;',
    '  font-weight: 600 !important;',
    '  cursor: pointer;',
    '  transition: background-color 0.18s ease;',
    '}',
    '.pagination-button:hover, button[class*="pagination"]:hover {',
    '  background-color: #ddc0b4 !important;',
    '}',
    'select {',
    '  background-color: #f1ede8 !important;',
    '  color: #1c1c1c !important;',
    '}',
    /* Anything Flashy fills with the brand colour keeps a readable label on it. */
    '.bg-primary, [class*="bg-primary"] {',
    '  color: #5C3228 !important;',
    '}',
    /* Their neutral greys sit cold against a warm page. */
    'p, span, li, label, small {',
    '  color: inherit;',
    '}',
    'hr, [class*="border"] {',
    '  border-color: rgba(72, 72, 72, 0.15) !important;',
    '}',
    /* The theme already prints the band's heading (.product-reviews__title, in the
       brand face); the widget's own h2 said "ביקורות" a second time right under it. */
    'header h2 {',
    '  display: none !important;',
    '}'
  ].join('\n');

  /* ZERO REVIEWS — hiding removed at Guy's request, 7.9.2026.
     This used to hide the reviews band and the star line while the count was zero.
     Guy has a reviews automation going live and wants the band on the page, so the
     widget shows whatever Flashy returns, including its own empty state. Anything
     the band shows in that state is Flashy's, not the theme's.
     Left as a no-op rather than deleted so the call sites stay readable. */
  function hideIfEmpty(host) {
    var band = host && host.closest('.product-reviews');
    if (band) band.hidden = false;
    var stars = document.querySelectorAll('flashy-product-rating-stars');
    for (var i = 0; i < stars.length; i++) stars[i].hidden = false;
  }

  function paint(host) {
    var sr = host && host.shadowRoot;
    if (!sr || sr[MARK]) return;
    // adoptedStyleSheets is the only way in: the widget ships no <style> tags of its
    // own, its rules all arrive as constructable sheets.
    if (!('adoptedStyleSheets' in sr) || typeof CSSStyleSheet === 'undefined') return;
    try {
      var sheet = new CSSStyleSheet();
      sheet.replaceSync(CSS);
      // Appended, never replacing: Flashy's three sheets stay exactly as they are and
      // this one only wins where it is more specific or marked important.
      sr.adoptedStyleSheets = sr.adoptedStyleSheets.concat([sheet]);
      sr[MARK] = true;
    } catch (e) {
      /* A browser without constructable stylesheets simply keeps Flashy's own look,
         which is a fair fallback: slightly off-brand beats unstyled or broken. */
    }
  }

  function scan() {
    var found = document.getElementsByTagName(TAG);
    for (var i = 0; i < found.length; i++) { paint(found[i]); hideIfEmpty(found[i]); }
    return found.length;
  }

  scan();
  // The element is injected by Flashy's script well after DOMContentLoaded, its shadow
  // tree fills in later still, and it can be replaced when the widget re-renders, so
  // watch rather than poll. Stop once the page has settled; an observer left on the
  // whole document for the life of the session is a cost with no remaining benefit.
  var obs = new MutationObserver(function () { scan(); });
  obs.observe(document.documentElement, { childList: true, subtree: true });
  var ticks = 0;
  var timer = setInterval(function () {
    scan();
    if (++ticks >= 10) { clearInterval(timer); obs.disconnect(); }
  }, 2000);
})();
